import {
  Typography,
  Input,
  Button,
  Avatar,
  Spin,
  Tag,
  Tooltip,
  Descriptions,
  Tour,
  TourProps,
  InputRef,
  message,
} from "antd";
import { usePermissions } from "./PermissionsProvider";
import "./ChatComponent.css"; // Import CSS for styling
import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { SandboxMessage, SandboxSession } from "./model_gen/sandbox";
import {
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ScissorOutlined,
  CloseCircleOutlined,
  AudioOutlined,
  BranchesOutlined,
} from "@ant-design/icons";
import { SandboxService } from "./services/SandboxService";
import { EvaluateResponse, EvaluateResult } from "./model_gen/evaluation";
import { Sentinel } from "./model_gen/sentinel";
import MaitaiIcon from "./assets/maitai_icon_filled.svg";
import MetaLogo from "./assets/meta_primary.svg";
import { v4 as uuidv4 } from "uuid";
import ReconnectingWebSocket from "reconnecting-websocket";
import { GeneralService } from "./services/GeneralService";
import { ConfigService } from "./services/ConfigService";
import { Agent } from "./model_gen/agent_base";

interface SyncChatComponentProps {
  evalResponses: EvaluateResponse[];
  setEvalResponses: React.Dispatch<React.SetStateAction<EvaluateResponse[]>>;
  evalResponsesRef: React.MutableRefObject<EvaluateResponse[]>;
  ref: any;
  models: string[];
  temperature: number;
  evaluations: boolean;
  autocorrect: boolean;
  retrieveContext: boolean;
  streaming: boolean;
  targetMessage: SandboxMessage | null;
  setTargetMessage: React.Dispatch<React.SetStateAction<SandboxMessage | null>>;
  openRightSider?: () => void;
  systemPrompt?: string;
  assistantFirst?: boolean;
  conversationSummaryMode?: boolean;
  context?: string;
  agentId?: number | null;
  agentSettings?: any | null;
  startConversationCounter?: number;
  applicationName?: string;
  onConversationChange?: (conversation: any) => void;
  onHasMessagesChange?: (hasMessages: boolean) => void;
}

interface Conversation {
  turns: Turn[];
}

interface Turn {
  sender: "USER" | "BOT";
  messages: SandboxMessage[];
}

interface ModelTiming {
  model: string;
  startTime: number;
  ttftTime: number | null;
  ttftValue: number | null;
  completionTime: number | null;
  inferenceTime: number | null;
}

interface TurnModelTimings {
  turnIndex: number;
  modelTimings: ModelTiming[];
}

interface InterruptionPoint {
  messageId: string | null;
  position: number | null;
}

interface TranscriptionResponse {
  text: string;
  status: "success" | "error";
  error?: string;
  message?: string;
  transcript?: string;
  is_final?: boolean;
  processing_time?: number;
  cleaning_time?: number;
  transcription_time?: number;
}

// Audio Visualization Component
const AudioVisualization: React.FC<{
  levels: number[];
  isRecording: boolean;
}> = ({ levels, isRecording }) => {
  if (!isRecording) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "2px",
        pointerEvents: "none",
      }}>
      {levels.map((level, index) => (
        <div
          key={index}
          style={{
            width: "2px",
            height: `${Math.max(2, level * 16)}px`,
            backgroundColor: "#ff4d4f",
            borderRadius: "1px",
            transition: "height 0.1s ease",
            opacity: 0.8 + level * 0.2,
          }}
        />
      ))}
    </div>
  );
};

const SyncChatComponent: React.FC<SyncChatComponentProps> = forwardRef(
  (
    {
      evalResponses,
      setEvalResponses,
      evalResponsesRef,
      models,
      temperature,
      evaluations,
      autocorrect,
      retrieveContext,
      streaming,
      targetMessage,
      setTargetMessage,
      openRightSider,
      systemPrompt,
      assistantFirst,
      conversationSummaryMode,
      context,
      agentId,
      agentSettings,
      startConversationCounter,
      applicationName,
      onConversationChange,
      onHasMessagesChange,
    },
    ref
  ) => {
  const { apiKey } = usePermissions();
  const [conversation, setConversation] = useState<Conversation>({
    turns: [],
  });
  const conversationRef = useRef<Conversation>({ turns: [] });
  const [inputValue, setInputValue] = useState("");
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);
  const [loading, setLoading] = useState(false);
  const [awaitingFirstMessage, setAwaitingFirstMessage] = useState(false);
  const [session, setSession] = useState<SandboxSession | undefined>(
    undefined
  );
  const [agents, setAgents] = useState<Agent[]>([]);
  const [restoreCheckpointOnNextMessage, setRestoreCheckpointOnNextMessage] = useState(false);
    const [strobeMessage, setStrobeMessage] = useState<{
      id: string | null;
      color: string;
    }>({ id: null, color: "" });
    const severityColor: Record<number, string> = {
      1: "default", // INFO
      2: "warning", // WARN
      3: "error", // ERROR
    };
    const faultRef = useRef<any>(null);
    const [tourOpen, setTourOpen] = useState(false);
    const [tourShown, setTourShown] = useState(false);
    const tourSteps: TourProps["steps"] = [
      {
        title: "FAULT FOUND!",
        description:
          "Maitai found a fault!\nHover over the fault badge to see the description and proposed corrections.",
        target: () => faultRef.current,
        nextButtonProps: {
          children: "Okay",
          onClick: () => {
            setTourOpen(false);
          },
        },
      },
    ];
    const [isTyping, setIsTyping] = useState(false);
    const processedTokenRefs = useRef<Record<string, boolean>>({});
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [modelTimings, setModelTimings] = useState<ModelTiming[]>([]);
    const [allTurnTimings, setAllTurnTimings] = useState<TurnModelTimings[]>(
      []
    );
    const modelTimingsRef = useRef<ModelTiming[]>([]);
    const [animationFrame, setAnimationFrame] = useState<number | null>(null);
    const [currentTime, setCurrentTime] = useState<number>(Date.now());
    const [interruptionPoints, setInterruptionPoints] = useState<
      InterruptionPoint[]
    >([]);
    const [interruptionMode, setInterruptionMode] = useState<boolean>(false);
    const [interruptionHelperText, setInterruptionHelperText] =
      useState<string>("");
    const [selectedMessageForInterruption, setSelectedMessageForInterruption] =
      useState<SandboxMessage | null>(null);
    const interruptionModeRef = useRef<boolean>(false);

    // Voice recording states and refs
    const [isRecording, setIsRecording] = useState(false);
    const [isConnecting, setIsConnecting] = useState(true);
    const wsRef = useRef<ReconnectingWebSocket | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const processorRef = useRef<ScriptProcessorNode | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const onMessageHandlerRef = useRef((_event: MessageEvent) => {});
    const pausedRecordingRef = useRef(false);
    // Add audio level visualization states
    const [audioLevels, setAudioLevels] = useState<number[]>([0, 0, 0, 0, 0]);
    const audioLevelAnimationRef = useRef<number | null>(null);

    // Model color mapping for consistency
    const modelColorMap: Record<string, string> = {
      "gpt-3.5-turbo": "#10a37f",
      "gpt-4": "#8e44ad",
      "gpt-4-turbo": "#9b59b6",
      "claude-3-opus": "#e67e22",
      "claude-3-sonnet": "#d35400",
      "claude-3-haiku": "#e74c3c",
      "mistral-medium": "#3498db",
      "mistral-small": "#2980b9",
      "maitai:llama-3.1-8b": "#21b892",
      "maitai:red_robin": "#21b892",
      "llama-3.1-8b": "#21b892",
      red_robin: "#21b892",
      "openai:gpt-4o-mini": "#ffa658",
      "gpt-4o-mini": "#ffa658",
      "maitai:predibase-llama-3.1-8b-instruct": "#ffa658",
      "predibase-llama-3.1-8b-instruct": "#ffa658",
    };

    // Model label mapping for display
    const modelLabelMap: Record<string, React.ReactNode> = {
      "maitai:red_robin": (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
          maitai-groq-lora
          <img
            src={MetaLogo}
            alt="Meta"
            style={{ height: "20px", marginLeft: "4px" }}
          />
        </span>
      ),
      "maitai:predibase-llama-3.1-8b-instruct": (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
          llama-3.1-8b
          <img
            src={MetaLogo}
            alt="Meta"
            style={{ height: "20px", marginLeft: "4px" }}
          />
        </span>
      ),
      "openai:gpt-4o-mini": "gpt-4o-mini",
    };

    // Get color for a model (with fallback)
    const getModelColor = (modelName: string): string => {
      const baseModelName = modelName?.toLowerCase().split(":").pop() || "";
      // Try exact match first
      if (baseModelName && modelColorMap[baseModelName]) {
        return modelColorMap[baseModelName];
      }
      // Try partial match
      const matchingModel = Object.keys(modelColorMap).find((model) =>
        baseModelName.includes(model)
      );
      return matchingModel ? modelColorMap[matchingModel] : "#666666"; // Default gray
    };

  // Get user-friendly label for a model
  const getModelLabel = (modelName: string): React.ReactNode => {
    // If there's a direct mapping, use it
    if (modelName && modelLabelMap[modelName]) {
      return modelLabelMap[modelName];
    }
    
    // Check if it's an agent model (format: "agent:123")
    if (modelName && modelName.startsWith("agent:")) {
      const agentId = modelName.split(":")[1];
      const agent = agents.find((a) => a.id?.toString() === agentId);
      if (agent && agent.agent_name) {
        return `agent:${agent.agent_name}`;
      }
      // Fallback to showing agent:ID if name not found
      return modelName;
    }
    
    // Otherwise fallback to extracting the model name from the string
    return modelName?.split(":").pop() || modelName || "";
  };

    // Function to determine timing color with separate thresholds for TTFT and inference time
    const getTimingColor = (time: number, isTTFT: boolean = false): string => {
      if (isTTFT) {
        // TTFT thresholds
        if (time < 300) return "#52c41a"; // Fast - green
        if (time < 400) return "#faad14"; // Medium - yellow
        return "#f5222d"; // Slow - red
      } else {
        // Inference time thresholds
        if (time <= 500) return "#52c41a"; // Fast - green
        if (time <= 650) return "#faad14"; // Medium - yellow
        return "#f5222d"; // Slow - red
      }
    };

    // Function to update current time for animation
    const updateCurrentTime = useCallback(() => {
      setCurrentTime(Date.now());
      const frame = requestAnimationFrame(updateCurrentTime);
      setAnimationFrame(frame);
    }, []);

    // Start/stop animation when model timings change
    useEffect(() => {
      modelTimingsRef.current = modelTimings;

      // If we have active timings (not all completionTime are set), start animation
      const hasActiveTimings =
        modelTimings.length > 0 &&
        modelTimings.some((t) => t.completionTime === null);

      if (hasActiveTimings && !animationFrame) {
        const frame = requestAnimationFrame(updateCurrentTime);
        setAnimationFrame(frame);
      } else if (!hasActiveTimings && animationFrame) {
        cancelAnimationFrame(animationFrame);
        setAnimationFrame(null);
      }

      // Cleanup animation frame on unmount
      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }, [modelTimings, animationFrame, updateCurrentTime]);

    // Effect to store turn timings when a turn is complete
    useEffect(() => {
      // Only add to allTurnTimings if we have completed timings
      if (
        modelTimings.length > 0 &&
        modelTimings.some(
          (timing) =>
            timing.ttftValue !== null ||
            timing.ttftTime !== null ||
            timing.inferenceTime !== null
        )
      ) {
        // Find the current turn index
        const currentTurnIndex = conversation.turns.length - 1;

        // Check if this turn already has timings
        const existingTurnIndex = allTurnTimings.findIndex(
          (t) => t.turnIndex === currentTurnIndex
        );

        if (existingTurnIndex >= 0) {
          // Update existing turn timings
          setAllTurnTimings((prev) => {
            const newTimings = [...prev];
            newTimings[existingTurnIndex] = {
              turnIndex: currentTurnIndex,
              modelTimings: [...modelTimings],
            };
            return newTimings;
          });
        } else {
          // Add new turn timings
          setAllTurnTimings((prev) => [
            ...prev,
            {
              turnIndex: currentTurnIndex,
              modelTimings: [...modelTimings],
            },
          ]);
        }
      }
    }, [modelTimings, conversation.turns.length]);

    // Helper function to get model segments for a specific set of timings
    const getModelSegmentsForTimings = useCallback(
      (turnModelTimings: ModelTiming[]) => {
        // Sort models by TTFT or inference time based on streaming setting
        const sortedTimings = [...turnModelTimings].sort((a, b) => {
          // If streaming is on, prioritize TTFT
          if (streaming) {
            // If we have TTFT values from metadata, use those for sorting
            if (a.ttftValue !== null && b.ttftValue !== null) {
              return a.ttftValue - b.ttftValue;
            }

            // If one has TTFT value and the other doesn't, prioritize the one with data
            if (a.ttftValue !== null) return -1;
            if (b.ttftValue !== null) return 1;

            // Fall back to calculated TTFT if available
            if (a.ttftTime !== null && b.ttftTime !== null) {
              return a.ttftTime - a.startTime - (b.ttftTime - b.startTime);
            }

            // If one has calculated TTFT and the other doesn't, prioritize the one with data
            if (a.ttftTime !== null) return -1;
            if (b.ttftTime !== null) return 1;
          }
          // If streaming is off, use inference times
          else {
            // If we have inference times, use those for sorting
            if (a.inferenceTime !== null && b.inferenceTime !== null) {
              return a.inferenceTime - b.inferenceTime;
            }

            // If one has inference time and the other doesn't, prioritize the one with data
            if (a.inferenceTime !== null) return -1;
            if (b.inferenceTime !== null) return 1;
          }

          // Fall back to completion time if available
          const aTime = a.completionTime || currentTime;
          const bTime = b.completionTime || currentTime;
          return aTime - a.startTime - (bTime - b.startTime);
        });

        // Calculate max time across all models (default minimum 500ms)
        const maxTimeValue = Math.max(
          500, // Minimum scale
          ...sortedTimings.map((timing) => {
            // Use TTFT from metadata if streaming is on and value is available
            if (streaming && timing.ttftValue !== null) {
              return timing.ttftValue;
            }
            // Fall back to calculated TTFT if available
            else if (streaming && timing.ttftTime !== null) {
              return timing.ttftTime - timing.startTime;
            }
            // Otherwise, use inference time if available
            else if (!streaming && timing.inferenceTime !== null) {
              return timing.inferenceTime;
            }
            // For incomplete responses, use current elapsed time
            return currentTime - timing.startTime;
          })
        );

        // Calculate total max time for scaling
        const maxTimeMs = maxTimeValue; // Use the dynamically calculated max

        // Calculate segments - adjust to create a timeline view
        let lastEndTime = 0;
        const segments = sortedTimings.map((timing, index) => {
          let duration: number;
          let displayDuration: number; // What's shown in the UI
          let isComplete: boolean;
          let startPercent = 0;
          let widthPercent = 0;

          // Determine which metric to use based on streaming setting
          if (streaming && timing.ttftValue !== null) {
            // If streaming is on, use TTFT from metadata
            duration = timing.ttftValue;
            displayDuration = duration;
            isComplete = true;
          } else if (streaming && timing.ttftTime !== null) {
            // Fall back to calculated TTFT if metadata ttft not available
            duration = timing.ttftTime - timing.startTime;
            displayDuration = duration;
            isComplete = true;
          } else if (!streaming && timing.inferenceTime !== null) {
            // If streaming is off, use inference time
            duration = timing.inferenceTime;
            displayDuration = duration;
            isComplete = streaming
              ? timing.ttftValue !== null || timing.ttftTime !== null
              : timing.inferenceTime !== null;
          } else {
            // For incomplete responses, just show elapsed time
            duration = currentTime - timing.startTime;
            displayDuration = duration;
            isComplete = streaming
              ? timing.ttftValue !== null || timing.ttftTime !== null
              : timing.inferenceTime !== null;
          }

          // For timeline view: first segment starts at 0
          if (index === 0) {
            startPercent = 0;
            widthPercent = Math.min(100, (duration / maxTimeMs) * 100);
            lastEndTime = duration;
          }
          // Subsequent segments start where the previous ended
          else {
            startPercent = Math.min(100, (lastEndTime / maxTimeMs) * 100);
            // Width is the difference between this completion and the last one
            const segmentDuration = Math.max(0, duration - lastEndTime);
            widthPercent = Math.min(
              100 - startPercent,
              (segmentDuration / maxTimeMs) * 100
            );
            lastEndTime = duration;
          }

          return {
            model: timing.model,
            percent: widthPercent,
            startPercent: startPercent,
            color: getModelColor(timing.model),
            duration: displayDuration,
            isComplete: isComplete,
            metricType: streaming ? "ttft" : "inference",
            maxTimeMs: maxTimeMs,
          };
        });

        return segments;
      },
      [streaming, currentTime]
    );

    // Get all model timing segments for the stacked progress bar for current turn
    const getModelSegments = useCallback(() => {
      return getModelSegmentsForTimings(modelTimings);
    }, [modelTimings, getModelSegmentsForTimings]);

    const handleMessageClick = (message: SandboxMessage) => {
      if (message.sender === "BOT") {
        setTargetMessage(message.id === targetMessage?.id ? null : message);
        if (isMobile && openRightSider && message.id !== targetMessage?.id) {
          openRightSider();
        }
      }
    };

    // Helper function to get all messages from conversation
    const getAllMessages = useCallback(() => {
      return conversation.turns.flatMap((turn) => turn.messages);
    }, [conversation]);

    // Helper function to get the last bot message for voice context
    const getLastBotMessage = useCallback(() => {
      const allMessages = conversation.turns.flatMap((turn) => turn.messages);
      console.log(
        "🔍 [DEBUG] All messages:",
        allMessages.map((msg) => ({
          sender: msg.sender,
          messagePreview: (msg.message || "").substring(0, 50) + "...",
        }))
      );

      // Find the last message from assistant/bot
      for (let i = allMessages.length - 1; i >= 0; i--) {
        const message = allMessages[i];
        console.log(
          `🔍 [DEBUG] Checking message ${i}: sender="${
            message.sender
          }", message="${(message.message || "").substring(0, 50)}..."`
        );
        if (
          message.sender?.toLowerCase() === "assistant" ||
          message.sender?.toLowerCase() === "bot"
        ) {
          console.log("✅ [DEBUG] Found last bot message:", message.message);
          return message.message || "";
        }
      }
      console.log("❌ [DEBUG] No bot messages found");
      return "";
    }, [conversation]);

    // Helper function to get the last turn
    const getLastTurn = useCallback(() => {
      return conversation.turns[conversation.turns.length - 1];
    }, [conversation]);

    // Helper function to update a message in the conversation
    const updateMessageInConversation = useCallback(
      (
        messageId: number | string,
        updateFn: (message: SandboxMessage) => SandboxMessage
      ) => {
        setConversation((prevConversation) => {
          const newTurns = prevConversation.turns.map((turn) => {
            const updatedMessages = turn.messages.map((msg) =>
              msg.id === messageId ? updateFn(msg) : msg
            );
            return { ...turn, messages: updatedMessages };
          });
          return { ...prevConversation, turns: newTurns };
        });
      },
      []
    );

    // Helper function to add a new turn to the conversation
    const addTurn = useCallback(
      (sender: "USER" | "BOT", messages: SandboxMessage[]) => {
        setConversation((prevConversation) => ({
          turns: [...prevConversation.turns, { sender, messages }],
        }));
      },
      []
    );

    // Helper function to update the last turn's messages
    const updateLastTurnMessages = useCallback(
      (updateFn: (messages: SandboxMessage[]) => SandboxMessage[]) => {
        setConversation((prevConversation) => {
          if (prevConversation.turns.length === 0) return prevConversation;

          const newTurns = [...prevConversation.turns];
          const lastTurn = newTurns[newTurns.length - 1];
          newTurns[newTurns.length - 1] = {
            ...lastTurn,
            messages: updateFn(lastTurn.messages),
          };
          return { ...prevConversation, turns: newTurns };
        });
      },
      []
    );

    useEffect(() => {
      conversationRef.current = conversation;
      scrollToBottom();
    }, [conversation]);

    useEffect(() => {
      evalResponsesRef.current = evalResponses;
      if (evalResponses.length > 0) {
        handleFaultFound();
      }
    }, [evalResponses]);

  useEffect(() => {
    // Add window resize listener to update isMobile state
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch agents to display agent names
  useEffect(() => {
    if (!apiKey) return;

    const agentsSubscription = ConfigService.getAgents(null, apiKey).subscribe(
      (response: any) => {
        const fetchedAgents = Array.isArray(response)
          ? response
          : response.agents || [];
        setAgents(fetchedAgents);
      },
      (error: any) => {
        console.error("Error fetching agents:", error);
      }
    );

    return () => {
      agentsSubscription.unsubscribe();
    };
  }, [apiKey]);

    useEffect(() => {
      // Start a new session only after the user clicks Start Conversation (counter > 0)
      if ((startConversationCounter || 0) > 0) {
        clearConversation();
        newSession();
      }
    }, [startConversationCounter, assistantFirst]);

    // Notify parent component when conversation changes
    useEffect(() => {
      if (onConversationChange) {
        onConversationChange(conversation);
      }
    }, [conversation, onConversationChange]);

    // Notify parent component when hasMessages status changes
    useEffect(() => {
      if (onHasMessagesChange) {
        const hasMessages = conversation.turns.some(turn => 
          turn.messages.some(msg => msg.message && msg.message.trim() !== "")
        );
        onHasMessagesChange(hasMessages);
      }
    }, [conversation, onHasMessagesChange]);

    const handleFaultFound = () => {
      if (!tourShown) {
        window.heap.track("Tour Shown - Fault");
        setTourShown(true);
        setTourOpen(true);
      }
    };

    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop =
          messagesContainerRef.current.scrollHeight;
      }
    };

    const newSession = () => {
      setLoading(true);
      const session: SandboxSession = {
        sandbox_application_id: null,
        meta: {
          assistant_first: assistantFirst,
          system_prompt: systemPrompt,
          model: models[0],
          temperature: temperature,
          conversation_summary_mode: conversationSummaryMode,
        },
      };

      // If assistant should not make first message, don't wait for first message
      setAwaitingFirstMessage(assistantFirst == true);

      // Use the streaming session endpoint
      const subscription = SandboxService.newSessionStream(
        session,
        apiKey,
        applicationName
      ).subscribe(
        (result: any) => {
          if (result.type === "session") {
            setSession(result.data);
            if (!assistantFirst) {
              setLoading(false);
            }
          }
          if (result.type === "bot_message") {
            addTurn("BOT", [result.data]);
            setLoading(false);
          }
        },
        (error: any) => {
          console.error("Error creating session", error);
          setLoading(false);
        }
      );

      return () => subscription.unsubscribe();
    };

    // Add helper function for number spacing
    const addNumberSpacing = (text: string | null | undefined): string => {
      if (!text) return "";
      // Add space before a number that follows a letter
      text = text.replace(/([a-zA-Z])(\d)/g, "$1 $2");
      // Add space after a number that precedes a letter
      text = text.replace(/(\d)([a-zA-Z])/g, "$1 $2");
      return text;
    };

    // Helper function to handle token updates consistently
    const handleTokenUpdate = useCallback(
      (tokenUpdate: SandboxMessage, sessionObj: SandboxSession | undefined) => {
        if (!tokenUpdate.message || tokenUpdate.message.trim() == "") {
          return null;
        }

        // Create a unique token identifier to prevent duplicate processing
        const tokenId = `${tokenUpdate.meta?.reference_id}_${
          tokenUpdate.meta?.token_id || uuidv4()
        }`;

        // Skip if we've already processed this exact token
        if (processedTokenRefs.current[tokenId]) {
          return null;
        }

        // Mark this token as processed
        processedTokenRefs.current[tokenId] = true;

        // Only apply number spacing for Red Robin model
        const modelName = (tokenUpdate.meta?.model as string) || "";

        // Create a unique rendering key based on message content length
        const messageKey = `${tokenUpdate.meta?.reference_id}_${
          tokenUpdate.message?.length || 0
        }`;

        setConversation((prevConversation) => {
          const turns = [...prevConversation.turns];
          const lastTurn = turns[turns.length - 1];
          const newTokenMessage = modelName.toLowerCase().includes("red_robin")
            ? addNumberSpacing(tokenUpdate.message)
            : tokenUpdate.message;

          // Check if there's already a bot turn with this reference_id
          if (lastTurn && lastTurn.sender === "BOT") {
            const existingMessageIndex = lastTurn.messages.findIndex(
              (msg) => msg.meta?.reference_id === tokenUpdate.meta?.reference_id
            );

            if (existingMessageIndex >= 0) {
              // Update existing message with new token
              const updatedMessages = [...lastTurn.messages];
              const existingMessage = updatedMessages[existingMessageIndex];
              const newMessage =
                (existingMessage.message || "") + newTokenMessage;
              updatedMessages[existingMessageIndex] = {
                ...existingMessage,
                message: modelName.toLowerCase().includes("red_robin")
                  ? addNumberSpacing(newMessage)
                  : newMessage,
                meta: {
                  ...existingMessage.meta,
                  ...tokenUpdate.meta,
                  streaming: true,
                  renderKey: messageKey,
                  session: sessionObj,
                },
              };
              turns[turns.length - 1] = {
                ...lastTurn,
                messages: updatedMessages,
              };
            } else {
              // Add new message to existing BOT turn
              turns[turns.length - 1] = {
                ...lastTurn,
                messages: [
                  ...lastTurn.messages,
                  {
                    ...tokenUpdate,
                    message: newTokenMessage,
                    meta: {
                      ...tokenUpdate.meta,
                      streaming: true,
                      renderKey: messageKey,
                      session: sessionObj,
                    },
                  },
                ],
              };
            }
          } else {
            // Create new BOT turn
            turns.push({
              sender: "BOT",
              messages: [
                {
                  ...tokenUpdate,
                  message: newTokenMessage,
                  meta: {
                    ...tokenUpdate.meta,
                    streaming: true,
                    renderKey: messageKey,
                    session: sessionObj,
                  },
                },
              ],
            });
          }

          return { ...prevConversation, turns };
        });
      },
      []
    );

    // Update the handleMessageDoubleClick function
    const handleMessageDoubleClick = (
      message: SandboxMessage,
      event: React.MouseEvent
    ) => {
      // Only enable interruption mode on the last bot message
      const lastTurn = conversation.turns[conversation.turns.length - 1];
      if (lastTurn && lastTurn.sender === "BOT") {
        const isLastBotMessage = lastTurn.messages.some(
          (msg) => msg.meta?.reference_id === message.meta?.reference_id
        );

        if (isLastBotMessage) {
          setInterruptionMode(true);
          setInterruptionHelperText(
            "Select a position and click Save to set interruption point"
          );
          setSelectedMessageForInterruption(message);
          // Prevent the default double click behavior
          event.preventDefault();
          event.stopPropagation();
        }
      }
    };

    // Update the handleSetInterruptionPoint function to properly define tempInterruptionPosition as number
    const handleSetInterruptionPoint = (
      message: SandboxMessage,
      event: React.MouseEvent
    ) => {
      if (!interruptionMode) return;

      // Get the clicked position within the text
      const target = event.target as HTMLElement;

      // Get the click coordinates relative to the target element
      const clickX = event.clientX - target.getBoundingClientRect().left;

      // Estimate character position based on average character width
      const text = message.message || "";
      const textWidth = target.getBoundingClientRect().width;
      const avgCharWidth = textWidth / text.length;
      const charPosition = Math.round(clickX / avgCharWidth);

      // Ensure position is within text bounds
      const position = Math.max(0, Math.min(charPosition, text.length));

      // Just store the position without setting it yet
      setSelectedMessageForInterruption({
        ...message,
        meta: {
          ...message.meta,
          tempInterruptionPosition: position as number,
        },
      });
    };

    // Then fix the saveInterruptionPoint function type handling
    const saveInterruptionPoint = () => {
      if (!selectedMessageForInterruption) return;

      const position = selectedMessageForInterruption.meta
        ?.tempInterruptionPosition as number | undefined;
      if (position === undefined) return;

      const messageId = String(
        selectedMessageForInterruption.meta?.reference_id ||
          selectedMessageForInterruption.id
      );

      // Check if this message already has an interruption point
      setInterruptionPoints((prevPoints) => {
        const existingPointIndex = prevPoints.findIndex(
          (p) => p.messageId === messageId
        );

        if (existingPointIndex >= 0) {
          // Update existing point
          const newPoints = [...prevPoints];
          newPoints[existingPointIndex] = {
            messageId,
            position,
          };
          return newPoints;
        } else {
          // Add new point
          return [
            ...prevPoints,
            {
              messageId,
              position,
            },
          ];
        }
      });

      // Exit interruption mode
      setInterruptionMode(false);
      setInterruptionHelperText("");
      setSelectedMessageForInterruption(null);
    };

    // Modify the continueWithSending function to remove the line that clears the interruption point
    const continueWithSending = (tmp: string) => {
      // Reset model timings for new message
      setModelTimings([]);

      // Create a temporary new message with a temporary ID
      const tempId = Date.now();
      const tempNewMessage: SandboxMessage = {
        id: tempId,
        message: tmp,
        sender: "USER",
        sandbox_session_id: session?.id,
        meta: {
          session: session,
        },
      };

      // Add the temporary message to the conversation
      addTurn("USER", [tempNewMessage]);
      setInputValue("");
      setIsTyping(true);

      // Ensure we have at least one model to process
      if (!models || models.length === 0) {
        console.error("[SyncChatComponent] No models selected");
        setIsTyping(false);
        return;
      }

      // Process each model in sequence
      models.forEach((model) => {
        handleSendForModel(model, tmp, tempId);
      });
    };

    const handleSend = (override: string | null = null) => {
      const tmp = override || inputValue;
      if (!tmp.trim()) return;

      // If we already have a session or it's an assistant-first conversation, send message directly
      continueWithSending(tmp);
    };

    // Add an effect to maintain focus after sending messages
    useEffect(() => {
      // Only attempt to focus if we have a session and aren't in loading state
      if (session && !loading && !isTyping) {
        // Use a longer timeout to ensure UI updates have completed
        const focusTimer = setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);

        return () => clearTimeout(focusTimer);
      }
    }, [isTyping, loading, session]);

    // Add a new function to handle keyboard events at the document level
    useEffect(() => {
      const handleDocumentKeyDown = (e: KeyboardEvent) => {
        // If no element has focus and a key is pressed, focus the input
        if (
          document.activeElement === document.body &&
          e.key !== "Tab" &&
          !e.ctrlKey &&
          !e.altKey &&
          !e.metaKey
        ) {
          if (inputRef.current && session && !loading && !isTyping) {
            inputRef.current.focus();
          }
        }
      };

      document.addEventListener("keydown", handleDocumentKeyDown);
      return () =>
        document.removeEventListener("keydown", handleDocumentKeyDown);
    }, [session, loading, isTyping]);

    // Add another effect to auto-focus on the input when the typing state changes
    useEffect(() => {
      if (!isTyping && inputRef.current && session) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }, [isTyping, session]);

    const handleSendForModel = (model: string, tmp: string, tempId: number) => {
      // Format previous messages to include only relevant data and only messages from the current model or user
      // Apply interruption if applicable
      const conversationWithInterruption = getMessagesWithInterruption();

      const cleanedMessages = conversationWithInterruption
        .reduce((acc: SandboxMessage[], turn, turnIndex) => {
          const relevantMessages = turn.messages.filter(
            (msg) =>
              msg.sender === "USER" || // Always include user messages
              msg.meta?.model === model || // Include messages from current model
              (turnIndex === 0 && turn.sender === "BOT") || // Include first bot turn (greeting)
              (agentId != null && turn.sender === "BOT") // Include all bot messages when using agents
          );
          return [...acc, ...relevantMessages];
        }, [])
        .map((msg) => ({
          id: msg.id,
          message: msg.message,
          sender: msg.sender,
          sandbox_session_id: msg.sandbox_session_id,
          meta: msg.meta,
        }));

      // Send the message to the server without the temporary ID
      const messageToSend: Omit<SandboxMessage, "id"> = {
        message: tmp,
        sender: "USER",
        sandbox_session_id: session?.id,
        sandbox_application_id: session?.sandbox_application_id,
        session_token: session?.token,
        company_id: -1,
        meta: {
          model: model,
          temperature: temperature,
          evaluations: evaluations,
          autocorrect: autocorrect,
          retrieve_context: retrieveContext,
          streaming: streaming,
          client_start_time: Date.now(),
          session: session,
          agent_id: agentId ?? null,
          agent_settings: agentSettings ?? null,
          // Only send checkpoint restoration flag on the first message after branching
          restore_checkpoint: restoreCheckpointOnNextMessage,
        },
      };

      // Clear the restore checkpoint flag after using it once
      if (restoreCheckpointOnNextMessage) {
        setRestoreCheckpointOnNextMessage(false);
      }

      const messageCount = cleanedMessages.length;
      window.heap.track("New Message", {
        sender: "USER",
        message: tmp,
        session_id: agentId != null ? session?.token : session?.id,
      });

      // Add model timing entry when sending message
      setModelTimings((prev) => [
        ...prev,
        {
          model,
          startTime: Date.now(),
          ttftTime: null,
          ttftValue: null,
          completionTime: null,
          inferenceTime: null,
        },
      ]);

      const subscription = SandboxService.newMessageStream(
        messageToSend,
        cleanedMessages,
        apiKey
      ).subscribe(
        (response: any) => {
          var client_first_token_time = -1;
          if (response.type === "user_message") {
            // Replace the temporary user message with the real one
            updateMessageInConversation(tempId, () => response.data);

            window.heap.track("User Message Stored", {
              message_id: response.data.id,
              session_id: agentId ? session?.token : session?.id,
            });
          } else if (response.type === "token_update") {
            if (client_first_token_time === -1) {
              client_first_token_time = Date.now();

              // Update model timing with TTFT time (but don't set ttftValue yet)
              const modelName = (response.data?.meta?.model as string) || "";

              setModelTimings((prev) =>
                prev.map((timing) =>
                  timing.model === modelName && timing.ttftTime === null
                    ? {
                        ...timing,
                        ttftTime: client_first_token_time,
                      }
                    : timing
                )
              );
            }
            handleTokenUpdate(
              response.data,
              session
            );
            setIsTyping(false);
          } else if (response.type === "bot_message") {
            // This should be the final complete message after streaming is done
            const botMessage = response.data;
            botMessage.meta["client_end_time"] = Date.now();
            if (botMessage.meta?.ttft_diff) {
              botMessage.meta["client_end_time"] =
                botMessage.meta["client_end_time"] -
                botMessage.meta["ttft_diff"];
            }
            // Update model timing with completion time and inference time
            const modelName = (botMessage.meta?.model as string) || "";
            const infTime = botMessage.meta?.inf_time as number;
            const ttftFromMeta = botMessage.meta?.ttft as number;

            setModelTimings((prev) =>
              prev.map((timing) =>
                timing.model === modelName && timing.completionTime === null
                  ? {
                      ...timing,
                      completionTime: Date.now(),
                      inferenceTime: infTime || null,
                      ttftValue: ttftFromMeta || null,
                    }
                  : timing
              )
            );

            setConversation((prevConversation) => {
              const turns = [...prevConversation.turns];
              const lastTurn = turns[turns.length - 1];

              if (lastTurn && lastTurn.sender === "BOT") {
                const existingMessageIndex = lastTurn.messages.findIndex(
                  (msg) =>
                    msg.meta?.reference_id === botMessage.meta?.reference_id
                );

                if (existingMessageIndex >= 0) {
                  const updatedMessages = [...lastTurn.messages];
                  const existingMessage = updatedMessages[existingMessageIndex];
                  updatedMessages[existingMessageIndex] = {
                    ...existingMessage,
                    ...botMessage,
                    message:
                      existingMessage.message || botMessage.message || "",
                    meta: {
                      ...existingMessage.meta,
                      ...botMessage.meta,
                      streaming: false,
                      session: session,
                    },
                  };
                  turns[turns.length - 1] = {
                    ...lastTurn,
                    messages: updatedMessages,
                  };
                } else {
                  turns[turns.length - 1] = {
                    ...lastTurn,
                    messages: [
                      ...lastTurn.messages,
                      {
                        ...botMessage,
                        meta: {
                          ...botMessage.meta,
                          streaming: false,
                          session: session,
                        },
                      },
                    ],
                  };
                }
              } else {
                turns.push({
                  sender: "BOT",
                  messages: [
                    {
                      ...botMessage,
                      meta: {
                        ...botMessage.meta,
                        streaming: false,
                        session: session,
                      },
                    },
                  ],
                });
              }

              return { ...prevConversation, turns };
            });

            setIsTyping(false);
            window.heap.track("New Message", {
              sender: "BOT",
              message: botMessage.message,
              session_id: agentId ? session?.token : session?.id,
            });
          } else if (response.type === "evaluation") {
            const evalResponse = response.data;
            // Search directly in the conversation for the message
            setConversation((prevConversation) => {
              let foundMessage = false;
              const newTurns = prevConversation.turns.map((turn) => {
                const messageInTurn = turn.messages.find(
                  (msg) =>
                    String(msg.meta?.reference_id) ===
                    String(evalResponse?.reference_id)
                );

                if (messageInTurn) {
                  foundMessage = true;
                  console.log(
                    "[SyncChatComponent] Found message to evaluate in conversation"
                  );

                  const evaluation_results_set: EvaluateResult[] =
                    evalResponse.evaluation_results || [];
                  let hasFault = false;

                  // Create updated message with evaluation results
                  const updatedMessage = { ...messageInTurn };
                  if (evalResponse.correction && updatedMessage.meta) {
                    updatedMessage.meta = {
                      ...updatedMessage.meta,
                      correction: evalResponse.correction,
                    };
                  }

                  for (const eval_result of evaluation_results_set) {
                    if (
                      eval_result.meta &&
                      typeof eval_result.meta === "string"
                    ) {
                      eval_result.meta = JSON.parse(eval_result.meta);
                    }
                    if (eval_result?.status === "FAULT") {
                      hasFault = true;
                      console.log(
                        "[SyncChatComponent] Found fault in evaluation"
                      );

                      window.heap.track("Fault Found", {
                        session_id: agentId ? session?.token : session?.id,
                        sentinel: eval_result.sentinel_name,
                        severity: eval_result.severity?.toString() || "",
                        fault_description: eval_result.description,
                        fault_correction: eval_result.meta?.correction || "",
                        associated_message: updatedMessage.message,
                      });

                      if (
                        updatedMessage.meta &&
                        Array.isArray(updatedMessage.meta.eval_results)
                      ) {
                        updatedMessage.meta.eval_results = [
                          ...updatedMessage.meta.eval_results,
                          eval_result,
                        ];
                        updatedMessage.meta.fault_found = true;
                      } else {
                        updatedMessage.meta = {
                          ...updatedMessage.meta,
                          eval_results: [eval_result],
                          fault_found: true,
                        };
                      }
                    }
                  }

                  if (!hasFault && !updatedMessage.meta?.eval_results) {
                    console.log(
                      "[SyncChatComponent] No fault found, adding eval_result with lowest eval_time"
                    );
                    const eval_result = evaluation_results_set.sort(
                      (a, b) => (a.eval_time || 0) - (b.eval_time || 0)
                    )[0];
                    updatedMessage.meta = {
                      ...updatedMessage.meta,
                      eval_results: [eval_result],
                      fault_found: false,
                    };
                  }

                  const strobeColor = hasFault ? "red" : "green";
                  setStrobeMessage({
                    id: String(evalResponse?.reference_id),
                    color: strobeColor,
                  });
                  setTimeout(
                    () => setStrobeMessage({ id: null, color: "" }),
                    5000
                  );

                  if (hasFault) {
                    setEvalResponses(
                      (prevEvalResponses: EvaluateResponse[]) => {
                        const exists = prevEvalResponses.some(
                          (er) => er.reference_id === evalResponse.reference_id
                        );

                        if (!exists) {
                          return [...prevEvalResponses, evalResponse];
                        }
                        return prevEvalResponses;
                      }
                    );
                  }

                  return {
                    ...turn,
                    messages: turn.messages.map((msg) =>
                      String(msg.meta?.reference_id) ===
                      String(evalResponse?.reference_id)
                        ? updatedMessage
                        : msg
                    ),
                  };
                }
                return turn;
              });

              return { ...prevConversation, turns: newTurns };
            });
          }
        },
        (error: any) => {
          console.error("Error in message stream:", error);
          setIsTyping(false);
        },
        () => {
          console.log("[SyncChatComponent] Message stream completed");
          setIsTyping(false);
        }
      );

      return subscription;
    };

    useImperativeHandle(
      ref,
      () => {
        console.log("[SyncChatComponent] Setting up imperative handle");
        return {
          sendFaultyMessage: (sentinel: Sentinel) => {
            console.log("[SyncChatComponent] sendFaultyMessage called via ref");
            sendFaultyMessage(sentinel);
          },
          clearConversation: () => {
            console.log("[SyncChatComponent] clearConversation called via ref");
            clearConversation();
            newSession();
          },
        };
      },
      [session, systemPrompt, assistantFirst]
    );

    const clearConversation = () => {
      console.log("[SyncChatComponent] Clearing conversation");
      // Save current system prompt and assistant first settings to allow restart
      const currentSystemPrompt = systemPrompt;
      const currentAssistantFirst = assistantFirst;
      console.log(
        `[SyncChatComponent] Saved settings: systemPrompt=${currentSystemPrompt}, assistantFirst=${currentAssistantFirst}`
      );

      setConversation({ turns: [] });
      conversationRef.current = { turns: [] };
      // Reset processed tokens
      processedTokenRefs.current = {};
      // Reset model timings
      setModelTimings([]);
      // Reset all turn timings
      setAllTurnTimings([]);
      // Clear eval responses
      setEvalResponses([]);
      // Clear target message
      setTargetMessage(null);

      setSession(undefined);

      // Track refresh event
      window.heap.track("Session Refreshed");
    };

    const handleBranchFromTurn = (turnIndex: number, msg: SandboxMessage) => {
      if (!session || !agentId) {
        message.error("Cannot branch: session or agent not available");
        return;
      }

      // Calculate turn_number (0-indexed count of assistant messages up to and including this turn)
      let turnNumber = 0;
      for (let i = 0; i <= turnIndex; i++) {
        const turn = conversation.turns[i];
        if (turn && turn.sender === "BOT") {
          turnNumber++;
        }
      }
      // Turn number should be the count AFTER this turn completes
      // So it's the 0-indexed position of the last assistant message in this turn

      // Get the session_id from the message's meta (if it exists from a previous branch)
      // Otherwise fall back to the current session
      const messageSession = msg.meta?.session as SandboxSession | undefined;
      const sessionIdForBranch = messageSession?.token || messageSession?.id || session.token || session.id;

      console.log(
        `[SyncChatComponent] Branching from turn ${turnIndex}, turn_number=${turnNumber}, session=${sessionIdForBranch}, message_session=${messageSession?.token || messageSession?.id}, current_session=${session.token || session.id}`
      );

      // Call branch endpoint
      const subscription = SandboxService.branchSession(
        -1, // company_id (demo uses -1)
        agentId,
        String(sessionIdForBranch),
        turnNumber - 1, // Subtract 1 because we want to branch FROM this turn (not after it)
        session.sandbox_application_id || null,
        apiKey
      ).subscribe(
        (result: any) => {
          console.log("[SyncChatComponent] Branch response:", result);
          if (result.session) {
            // Clear current conversation
            clearConversation();

            // Set new session from branch
            setSession(result.session);

            // Set flag to restore checkpoint on the NEXT message only
            setRestoreCheckpointOnNextMessage(true);

            // Rebuild conversation up to the branch point
            const branchedConversation: Conversation = {
              turns: conversation.turns.slice(0, turnIndex + 1),
            };
            setConversation(branchedConversation);
            conversationRef.current = branchedConversation;

            message.success(
              `Branched from turn ${turnIndex + 1}. You can now continue from here with a new conversation path.`
            );

            window.heap.track("Session Branched", {
              parent_session_id: session.token || session.id,
              new_session_id: result.session.token || result.session.id,
              turn_number: turnNumber - 1,
            });
          }
        },
        (error: any) => {
          console.error("[SyncChatComponent] Error branching session:", error);
          message.error("Failed to branch conversation. Please try again.");
        }
      );

      return () => subscription.unsubscribe();
    };

    const sendFaultyMessage = (sentinel: Sentinel) => {
      const msg = `<${sentinel.sentinel_name}>`;
      handleSend(msg);
    };

    const applyEvaluation = (
      messages: SandboxMessage[],
      evalResponse: EvaluateResponse
    ) => {
      // Search through all turns and messages to find the matching message
      let foundMessage = false;
      let hasFault = false;

      setConversation((prevConversation) => {
        const newTurns = prevConversation.turns.map((turn) => {
          const messageInTurn = turn.messages.find((msg) => {
            const matches =
              String(msg.meta?.reference_id) ===
              String(evalResponse?.reference_id);
            return matches;
          });

          if (messageInTurn) {
            foundMessage = true;
            console.log(
              "[SyncChatComponent] Found message to evaluate in turn"
            );

            const evaluation_results_set: EvaluateResult[] =
              evalResponse.evaluation_results || [];

            // Create updated message with evaluation results
            const updatedMessage = { ...messageInTurn };
            if (evalResponse.correction && updatedMessage.meta) {
              updatedMessage.meta = {
                ...updatedMessage.meta,
                correction: evalResponse.correction,
              };
            }

            for (const eval_result of evaluation_results_set) {
              if (eval_result.meta && typeof eval_result.meta === "string") {
                eval_result.meta = JSON.parse(eval_result.meta);
              }
              if (eval_result?.status === "FAULT") {
                hasFault = true;
                console.log("[SyncChatComponent] Found fault in evaluation");

                window.heap.track("Fault Found", {
                  session_id: agentId ? session?.token : session?.id,
                  sentinel: eval_result.sentinel_name,
                  severity: eval_result.severity?.toString() || "",
                  fault_description: eval_result.description,
                  fault_correction: eval_result.meta?.correction || "",
                  associated_message: updatedMessage.message,
                });

                if (
                  updatedMessage.meta &&
                  Array.isArray(updatedMessage.meta.eval_results)
                ) {
                  updatedMessage.meta.eval_results = [
                    ...updatedMessage.meta.eval_results,
                    eval_result,
                  ];
                  updatedMessage.meta.fault_found = true;
                } else {
                  updatedMessage.meta = {
                    ...updatedMessage.meta,
                    eval_results: [eval_result],
                    fault_found: true,
                  };
                }
              }
            }

            if (!hasFault && !updatedMessage.meta?.eval_results) {
              console.log(
                "[SyncChatComponent] No fault found, adding eval_result with lowest eval_time"
              );
              const eval_result = evaluation_results_set.sort(
                (a, b) => (a.eval_time || 0) - (b.eval_time || 0)
              )[0];
              updatedMessage.meta = {
                ...updatedMessage.meta,
                eval_results: [eval_result],
                fault_found: false,
              };
            }

            return {
              ...turn,
              messages: turn.messages.map((msg) =>
                String(msg.meta?.reference_id) ===
                String(evalResponse?.reference_id)
                  ? updatedMessage
                  : msg
              ),
            };
          }
          return turn;
        });

        return { ...prevConversation, turns: newTurns };
      });

      const strobeColor = hasFault ? "red" : "green";
      setStrobeMessage({
        id: String(evalResponse?.reference_id),
        color: strobeColor,
      });
      setTimeout(() => setStrobeMessage({ id: null, color: "" }), 5000);

      if (hasFault) {
        console.log("[SyncChatComponent] Adding fault to evalResponses");
        setEvalResponses((prevEvalResponses: EvaluateResponse[]) => {
          const exists = prevEvalResponses.some(
            (er) => er.reference_id === evalResponse.reference_id
          );

          if (!exists) {
            return [...prevEvalResponses, evalResponse];
          }
          return prevEvalResponses;
        });
      }
    };

    // Function to remove interruption point
    const removeInterruptionPoint = (messageId: string) => {
      setInterruptionPoints((prevPoints) =>
        prevPoints.filter((point) => point.messageId !== messageId)
      );
    };

    // Add function to get messages with interruption applied
    const getMessagesWithInterruption = useCallback(() => {
      if (interruptionPoints.length === 0) {
        return conversation.turns;
      }

      return conversation.turns.map((turn) => ({
        ...turn,
        messages: turn.messages.map((msg) => {
          const msgId = String(msg.meta?.reference_id || msg.id);
          const interruptionPoint = interruptionPoints.find(
            (p) => p.messageId === msgId
          );

          if (interruptionPoint && interruptionPoint.position !== null) {
            return {
              ...msg,
              message:
                msg.message?.substring(0, interruptionPoint.position || 0) +
                " <INTERRUPTED>",
              meta: {
                ...msg.meta,
                interruption_point: interruptionPoint.position,
                original_message: msg.message,
              },
            };
          }
          return msg;
        }),
      }));
    }, [conversation.turns, interruptionPoints]);

    // Update the interruptionMode state to also update the ref
    useEffect(() => {
      interruptionModeRef.current = interruptionMode;
    }, [interruptionMode]);

    // Add keyboard event listener for arrow key navigation
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (!interruptionModeRef.current || !selectedMessageForInterruption)
          return;

        // Only handle left and right arrow keys
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();

          const currentPosition =
            (selectedMessageForInterruption.meta
              ?.tempInterruptionPosition as number) || 0;
          const messageText = selectedMessageForInterruption.message || "";

          // Calculate new position
          let newPosition = currentPosition;
          if (event.key === "ArrowLeft") {
            newPosition = Math.max(0, currentPosition - 1);
          } else {
            newPosition = Math.min(messageText.length, currentPosition + 1);
          }

          // Update position if changed
          if (newPosition !== currentPosition) {
            setSelectedMessageForInterruption({
              ...selectedMessageForInterruption,
              meta: {
                ...selectedMessageForInterruption.meta,
                tempInterruptionPosition: newPosition as number,
              },
            });
          }
        }
      };

      // Add and remove event listener
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [selectedMessageForInterruption]);

    useEffect(() => {
      onMessageHandlerRef.current = (event: MessageEvent) => {
        console.log("Received websocket message:", event.data);
        if (pausedRecordingRef.current) {
          return;
        }
        try {
          const response = JSON.parse(event.data);
          if (
            response.type === "vad" &&
            response.event === "pause" &&
            response.status === "success"
          ) {
            pausedRecordingRef.current = true;
            handleSend(null);
            stopRecording();
          } else if (response.status === "success") {
            if (response.transcript) {
              setInputValue(response.transcript);
            }
          } else if (response.status === "error") {
            message.error(response.error || "Transcription error occurred");
          }
        } catch (error) {
          message.error("Error processing server response");
        }
      };
    });

    const initializeWebSocket = () => {
      if (wsRef.current) {
        return; // Already initialized
      }

      // Initialize WebSocket connection with reconnection
      wsRef.current = new ReconnectingWebSocket(
        `${GeneralService.BREEZE_ENDPOINT}/breeze/ws`,
        [],
        {
          maxRetries: 10,
          reconnectionDelayGrowFactor: 1.3,
          maxReconnectionDelay: 10000,
          minReconnectionDelay: 1000,
        }
      );

      wsRef.current.onopen = () => {
        setIsConnecting(false);
        message.success("Connected to transcription server");
      };

      wsRef.current.onclose = (event) => {
        setIsConnecting(true);
        message.error("Disconnected from transcription server");
      };

      wsRef.current.onerror = (error) => {
        message.error("Transcription WebSocket error occurred");
      };

      wsRef.current.onmessage = (event) => {
        onMessageHandlerRef.current(event);
      };
    };

    useEffect(() => {
      return () => {
        if (wsRef.current) {
          wsRef.current.close();
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((track) => track.stop());
          mediaStreamRef.current = null;
        }
      };
    }, []);

    const startRecording = async () => {
      pausedRecordingRef.current = false;
      try {
        // Initialize WebSocket connection when user starts recording
        initializeWebSocket();

        // Wait for WebSocket to connect before proceeding
        if (wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
          message.info("Connecting to transcription server...");
          // Wait for connection with timeout
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error("Connection timeout"));
            }, 10000);

            const checkConnection = () => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                clearTimeout(timeout);
                resolve();
              } else {
                setTimeout(checkConnection, 100);
              }
            };
            checkConnection();
          });
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: 16000,
          },
        });
        mediaStreamRef.current = stream;

        // Create audio context
        const audioContext = new AudioContext({
          sampleRate: 16000,
          latencyHint: "interactive",
        });
        audioContextRef.current = audioContext;

        // Create audio source from stream
        const source = audioContext.createMediaStreamSource(stream);

        // Create script processor for raw PCM data
        const processor = audioContext.createScriptProcessor(1024, 1, 1);
        processorRef.current = processor;

        processor.onaudioprocess = (e) => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            // Get the raw PCM data
            const inputData = e.inputBuffer.getChannelData(0);

            // Calculate audio level for visualization
            let sum = 0;
            for (let i = 0; i < inputData.length; i++) {
              sum += inputData[i] * inputData[i];
            }
            const rms = Math.sqrt(sum / inputData.length);
            const level = Math.min(1, rms * 10); // Amplify and clamp to 0-1

            // Update audio levels for visualization
            setAudioLevels((prevLevels) => {
              const newLevels = [...prevLevels.slice(1), level];
              return newLevels;
            });

            // Convert Float32Array to Int16Array
            const pcmData = new Int16Array(inputData.length);
            for (let i = 0; i < inputData.length; i++) {
              pcmData[i] = Math.max(
                -32768,
                Math.min(32767, Math.round(inputData[i] * 32767))
              );
            }

            // Convert to base64
            const base64Audio = btoa(
              String.fromCharCode.apply(
                null,
                Array.from(new Uint8Array(pcmData.buffer))
              )
            );

            const message = {
              type: "audio",
              audio: base64Audio,
              format: "raw",
              sampleRate: 16000,
              channels: 1,
            };
            wsRef.current.send(JSON.stringify(message));
          }
        };

        // Connect the nodes
        source.connect(processor);
        processor.connect(audioContext.destination);

        // Send start message with context and last bot message
        const lastBotMessage = getLastBotMessage();
        const contextValue = context?.trim() || undefined;
        const lastBotMessageValue = lastBotMessage.trim() || undefined;

        console.log("🎯 [DEBUG] Voice recording start:", {
          conversationTurns: conversation.turns.length,
          totalMessages: conversation.turns.flatMap((turn) => turn.messages)
            .length,
          contextValue: contextValue || "None",
          lastBotMessage: lastBotMessageValue || "None",
          lastBotMessageLength: lastBotMessageValue?.length || 0,
        });

        const startMessage = {
          type: "start",
          context: contextValue,
          full_conversation: lastBotMessageValue,
        };

        wsRef.current?.send(JSON.stringify(startMessage));

        setIsRecording(true);
        setInputValue(""); // Clear previous input value
        message.success("Started recording");
      } catch (err) {
        if (err instanceof Error && err.message === "Connection timeout") {
          message.error(
            "Failed to connect to transcription server. Please check if the server is running."
          );
        } else {
          message.error(
            "Failed to access microphone. Please check permissions."
          );
        }
      }
    };

    const stopRecording = () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current = null;
      }

      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      setIsRecording(false);
      // Reset audio levels when stopping
      setAudioLevels([0, 0, 0, 0, 0]);

      // Send stop message to force final transcription
      wsRef.current?.send(JSON.stringify({ type: "stop" }));
      message.success("Stopped recording");
    };

    return (
      <div
        className="chatContainer"
        style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <style>{`
                .bot-response:hover .scissors-icon {
                    display: block !important;
                }
            `}</style>
        {interruptionMode && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              backgroundColor: "rgba(255, 0, 0, 0.1)",
              color: "#ff4d4f",
              padding: "8px",
              textAlign: "center",
              zIndex: 1000,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
            }}>
            <ScissorOutlined /> {interruptionHelperText}
            {selectedMessageForInterruption?.meta?.tempInterruptionPosition !==
              undefined && (
              <span style={{ fontSize: "12px", marginLeft: "8px" }}>
                Use ← → keys to adjust position
              </span>
            )}
            <Button
              size="small"
              type="primary"
              onClick={saveInterruptionPoint}
              style={{ marginLeft: "8px" }}
              danger>
              Save
            </Button>
            <Button
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => {
                setInterruptionMode(false);
                setInterruptionHelperText("");
                setSelectedMessageForInterruption(null);
              }}
              style={{ marginLeft: "4px" }}>
              Cancel
            </Button>
          </div>
        )}

        <div
          className="messagesContainer"
          ref={messagesContainerRef}
          style={{ position: "relative" }}>
          {loading && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}>
              <Spin size="large" />
            </div>
          )}
          {!session && !loading && conversation.turns.length === 0 && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}>
              <Typography
                style={{
                  fontSize: "18px",
                  color: "rgba(0,0,0,0.2)",
                  textAlign: "center",
                }}>
                Click "New Conversation" to start chatting
              </Typography>
            </div>
          )}
          {session &&
            !loading &&
            conversation.turns.length === 0 &&
            assistantFirst === false && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}>
                <Typography
                  style={{
                    fontSize: "18px",
                    color: "rgba(0,0,0,0.2)",
                    textAlign: "center",
                  }}>
                  Send a message to start the conversation
                </Typography>
              </div>
            )}
          {!loading && (
            <>
              {conversation.turns.map((turn, turnIndex) => (
                <div key={turnIndex} className="turn">
                  {turn.sender === "USER" ? (
                    // Render user messages as before
                    turn.messages
                      .filter(
                        (message) =>
                          message.message && message.message.trim() !== ""
                      )
                      .map((message, messageIndex) => (
                        <div key={message?.meta?.reference_id as string}>
                          <div
                            className={`message ${
                              strobeMessage?.id == String(message?.id)
                                ? `strobe-${strobeMessage?.color}`
                                : ""
                            }`}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              background:
                                message.id === targetMessage?.id
                                  ? "#fff"
                                  : "rgb(255, 255, 255, 0.4)",
                            }}
                            onClick={() => handleMessageClick(message)}
                            data-sender={message.sender}>
                            <div className="message-avatar">
                              <Avatar
                                icon={
                                  <UserOutlined style={{ color: "black" }} />
                                }
                                style={{
                                  background: "white",
                                  border: "1px solid rgba(0,0,0,0.1)",
                                }}
                              />
                            </div>
                            <div className="message-content">
                              <div
                                className="message-header"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}>
                                <strong>You</strong>
                              </div>
                              <div>
                                {typeof message.message === "string"
                                  ? message.message
                                  : JSON.stringify(message.message)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                  ) : (
                    // Modified bot message rendering to include interruption point
                    <div
                      className="message"
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        background: "rgb(255, 255, 255, 0.4)",
                      }}>
                      <div className="message-avatar">
                        <Avatar
                          src={MaitaiIcon}
                          style={{
                            padding: "5px",
                            background: "white",
                            border: "1px solid rgba(0,0,0,0.1)",
                          }}
                        />
                      </div>
                      <div
                        className="message-content"
                        style={{ width: "100%" }}>
                        <div className="message-header">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <strong>Maitai Bot</strong>

                              {/* Show interruption indicator if message has an interruption point */}
                              {interruptionPoints.some((point) =>
                                turn.messages.some(
                                  (msg) =>
                                    String(msg.meta?.reference_id || msg.id) ===
                                    point.messageId
                                )
                              ) && (
                                <Tag color="red" style={{ marginLeft: "8px" }}>
                                  <ScissorOutlined /> Interrupted
                                </Tag>
                              )}

                              {/* Add remove button if message has an interruption point */}
                              {turn.messages.filter((msg) =>
                                interruptionPoints.some(
                                  (point) =>
                                    String(msg.meta?.reference_id || msg.id) ===
                                    point.messageId
                                )
                              ).length > 0 && (
                                <Button
                                  size="small"
                                  danger
                                  type="link"
                                  onClick={() => {
                                    // Remove all interruption points for this turn
                                    turn.messages.forEach((msg) => {
                                      const msgId = String(
                                        msg.meta?.reference_id || msg.id
                                      );
                                      if (
                                        interruptionPoints.some(
                                          (point) => point.messageId === msgId
                                        )
                                      ) {
                                        removeInterruptionPoint(msgId);
                                      }
                                    });
                                  }}
                                  style={{ marginLeft: "4px" }}>
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection:
                              isMobile || models.length <= 1 || turnIndex === 0
                                ? "column"
                                : "row",
                            flexWrap:
                              isMobile || models.length <= 1 || turnIndex === 0
                                ? "nowrap"
                                : "wrap",
                            gap: "16px",
                          }}>
                          {turn.messages
                            .filter(
                              (message) =>
                                message.message && message.message.trim() !== ""
                            )
                            .map((message, messageIndex) => {
                              const modelName =
                                (message.meta?.model as string) || "";
                              const modelColor = getModelColor(modelName);
                              const ttft = message.meta?.ttft as number;
                              const infTime = message.meta?.inf_time as number;
                              const useHorizontalLayout =
                                !isMobile &&
                                models.length > 1 &&
                                turnIndex !== 0;
                              const flexBasis = useHorizontalLayout
                                ? `calc(${100 / models.length}% - ${
                                    ((models.length - 1) * 16) / models.length
                                  }px)`
                                : "100%";

                              // Check if this message has an interruption point
                              const hasInterruptionPoint =
                                interruptionPoints.some(
                                  (point) =>
                                    point.messageId ===
                                    String(
                                      message.meta?.reference_id || message.id
                                    )
                                );

                              // Check if message is in the last turn (for enabling interruption)
                              const isInLastTurn =
                                turnIndex === conversation.turns.length - 1;

                              return (
                                <div
                                  key={String(
                                    message.meta?.renderKey ||
                                      message?.meta?.reference_id ||
                                      message.id
                                  )}
                                  className={`bot-response ${
                                    strobeMessage?.id == String(message?.id)
                                      ? `strobe-${strobeMessage?.color}`
                                      : ""
                                  } ${
                                    interruptionMode
                                      ? "interruption-mode-active"
                                      : ""
                                  }`}
                                  style={{
                                    marginTop:
                                      (isMobile ||
                                        models.length <= 1 ||
                                        turnIndex === 0) &&
                                      messageIndex > 0
                                        ? "16px"
                                        : "0",
                                    borderTop:
                                      (isMobile ||
                                        models.length <= 1 ||
                                        turnIndex === 0) &&
                                      messageIndex > 0
                                        ? "1px solid rgba(0, 0, 0, 0.08)"
                                        : "none",
                                    paddingTop:
                                      (isMobile ||
                                        models.length <= 1 ||
                                        turnIndex === 0) &&
                                      messageIndex > 0
                                        ? "16px"
                                        : "0",
                                    flex: useHorizontalLayout
                                      ? `1 1 ${flexBasis}`
                                      : "1 0 100%",
                                    maxWidth: useHorizontalLayout
                                      ? flexBasis
                                      : "100%",
                                    border: useHorizontalLayout
                                      ? "1px solid rgba(0, 0, 0, 0.08)"
                                      : "none",
                                    borderRadius: useHorizontalLayout
                                      ? "8px"
                                      : "0",
                                    padding: useHorizontalLayout ? "12px" : "0",
                                    background:
                                      message.id === targetMessage?.id
                                        ? "#fff"
                                        : "transparent",
                                    position: "relative",
                                  }}
                                  onClick={(e) =>
                                    interruptionMode
                                      ? handleSetInterruptionPoint(message, e)
                                      : handleMessageClick(message)
                                  }
                                  onDoubleClick={(e) =>
                                    isInLastTurn
                                      ? handleMessageDoubleClick(message, e)
                                      : undefined
                                  }
                                  data-interruption-enabled={
                                    isInLastTurn ? "true" : "false"
                                  }>
                                  {/* Message content wrapper with conditional tooltip for last turn messages */}
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      padding: "0px 8px 0px 6px",
                                      marginBottom: "6px",
                                      borderLeft: `3px solid ${modelColor}`,
                                    }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}>
                                      <span
                                        style={{
                                          fontWeight: "bold",
                                          color: modelColor,
                                        }}>
                                        {getModelLabel(modelName)}
                                      </span>

                                      {message.meta &&
                                      message.meta?.is_correction ? (
                                        <span
                                          style={{
                                            marginLeft: "8px",
                                            fontWeight: "normal",
                                            color: "#ff4d4f",
                                          }}>
                                          CORRECTION
                                        </span>
                                      ) : message.sender === "BOT" &&
                                        message.meta?.fault_found === false ? (
                                        <CheckCircleOutlined
                                          style={{
                                            color: "green",
                                            marginLeft: "8px",
                                          }}
                                        />
                                      ) : null}
                                    </div>

                                    {/* Timing information */}
                                    {(ttft || infTime) && (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}>
                                        <ClockCircleOutlined
                                          style={{
                                            marginRight: "4px",
                                            fontSize: "12px",
                                            color: "rgba(0,0,0,0.45)",
                                          }}
                                        />
                                        <Tooltip
                                          title={
                                            ttft
                                              ? "Time to first token / Inference time"
                                              : "Inference time"
                                          }>
                                          <Tag style={{ margin: 0 }}>
                                            {ttft ? (
                                              <>
                                                <span
                                                  style={{
                                                    color: getTimingColor(
                                                      ttft,
                                                      true
                                                    ),
                                                  }}>
                                                  {ttft}
                                                </span>
                                                <span
                                                  style={{
                                                    color: "rgba(0,0,0,0.45)",
                                                  }}>
                                                  /
                                                </span>
                                              </>
                                            ) : null}
                                            <span
                                              style={{
                                                color: getTimingColor(
                                                  infTime || 0,
                                                  false
                                                ),
                                              }}>
                                              {infTime || 0}
                                            </span>
                                            <span
                                              style={{
                                                color: "rgba(0,0,0,0.45)",
                                                fontSize: "11px",
                                              }}>
                                              {" "}
                                              ms
                                            </span>
                                          </Tag>
                                        </Tooltip>
                                        
                                        {/* Branch from here button - only show for agent-based conversations and first message */}
                                        {agentId && messageIndex === 0 && (
                                          <Tooltip title="Branch from this point to create a new conversation path">
                                            <Button
                                              size="small"
                                              icon={<BranchesOutlined />}
                                              onClick={() => handleBranchFromTurn(turnIndex, message)}
                                              style={{
                                                marginLeft: "8px",
                                                fontSize: "12px",
                                              }}>
                                            </Button>
                                          </Tooltip>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {Boolean(
                                    message.meta &&
                                      Array.isArray(
                                        (message.meta as any).eval_results
                                      ) &&
                                      (message.meta as any).fault_found
                                  ) && (
                                    <div
                                      style={{
                                        display: "flex",
                                        flexWrap: "nowrap",
                                        alignItems: "start",
                                        marginBottom: "8px",
                                      }}>
                                      {(
                                        (message.meta as any)
                                          .eval_results as EvaluateResult[]
                                      ).map(
                                        (
                                          eval_result: EvaluateResult,
                                          index: number
                                        ) =>
                                          eval_result.sentinel_name ? (
                                            <Tooltip
                                              key={index}
                                              overlayClassName="custom-tooltip"
                                              title={
                                                <Descriptions
                                                  size="small"
                                                  bordered
                                                  column={1}>
                                                  <Descriptions.Item label="Description">
                                                    {eval_result.description}
                                                  </Descriptions.Item>
                                                  <Descriptions.Item label="Correction">
                                                    {typeof eval_result.meta
                                                      ?.correction === "string"
                                                      ? `"${eval_result.meta.correction}"`
                                                      : ""}
                                                  </Descriptions.Item>
                                                </Descriptions>
                                              }>
                                              <Tag
                                                color={
                                                  severityColor[
                                                    eval_result.severity || 1
                                                  ]
                                                }
                                                style={{ marginLeft: "8px" }}
                                                ref={
                                                  eval_result.status === "FAULT"
                                                    ? faultRef
                                                    : null
                                                }>
                                                {eval_result.sentinel_name}
                                              </Tag>
                                            </Tooltip>
                                          ) : null
                                      )}
                                    </div>
                                  )}

                                  {/* Add back the correction/uncorrection tag */}
                                  {message.sender === "BOT" &&
                                    (message.meta as any)?.eval_results &&
                                    (message.meta as any)?.fault_found && (
                                      <div style={{ marginBottom: "8px" }}>
                                        <Tooltip
                                          title={
                                            message.meta?.original
                                              ? `Original: "${message.meta.original}"`
                                              : "Output has faults that were not corrected."
                                          }>
                                          <Tag
                                            color={
                                              message.meta?.original
                                                ? "success"
                                                : "error"
                                            }
                                            style={{ marginLeft: "0px" }}>
                                            {message.meta?.original
                                              ? "Corrected"
                                              : "Uncorrected"}
                                          </Tag>
                                        </Tooltip>
                                      </div>
                                    )}

                                  {/* Message text with conditional tooltip for interruption-enabled messages */}
                                  {isInLastTurn && !interruptionMode ? (
                                    <Tooltip title="Double-click to set interruption point">
                                      <div
                                        style={{
                                          cursor: interruptionMode
                                            ? "text"
                                            : "auto",
                                          position: "relative",
                                        }}>
                                        {typeof message.message === "string"
                                          ? (() => {
                                              // Check if this message has an interruption point
                                              const interruptionPoint =
                                                interruptionPoints.find(
                                                  (p) =>
                                                    p.messageId ===
                                                    String(
                                                      message.meta
                                                        ?.reference_id ||
                                                        message.id
                                                    )
                                                );

                                              const hasInterruptionPoint =
                                                !!interruptionPoint;

                                              if (
                                                hasInterruptionPoint &&
                                                interruptionPoint?.position !==
                                                  null
                                              ) {
                                                return (
                                                  <>
                                                    <span>
                                                      {message.message.substring(
                                                        0,
                                                        interruptionPoint.position ||
                                                          0
                                                      )}
                                                    </span>
                                                    <span
                                                      className="interruption-marker"
                                                      style={{
                                                        display: "inline-block",
                                                        width: "2px",
                                                        height: "1em",
                                                        backgroundColor: "red",
                                                        verticalAlign: "middle",
                                                        marginLeft: "2px",
                                                        marginRight: "2px",
                                                      }}></span>
                                                    <span
                                                      style={{
                                                        textDecoration:
                                                          "line-through",
                                                        color: "#999",
                                                      }}>
                                                      {message.message.substring(
                                                        interruptionPoint.position ||
                                                          0
                                                      )}
                                                    </span>
                                                  </>
                                                );
                                              } else if (
                                                selectedMessageForInterruption
                                                  ?.meta?.reference_id ===
                                                  message.meta?.reference_id &&
                                                typeof selectedMessageForInterruption
                                                  ?.meta
                                                  ?.tempInterruptionPosition ===
                                                  "number"
                                              ) {
                                                return (
                                                  <>
                                                    <span>
                                                      {message.message.substring(
                                                        0,
                                                        selectedMessageForInterruption
                                                          .meta
                                                          .tempInterruptionPosition as number
                                                      )}
                                                    </span>
                                                    <span
                                                      className="interruption-marker"
                                                      style={{
                                                        display: "inline-block",
                                                        width: "2px",
                                                        height: "1em",
                                                        backgroundColor:
                                                          "orange",
                                                        verticalAlign: "middle",
                                                        marginLeft: "2px",
                                                        marginRight: "2px",
                                                      }}></span>
                                                    <span
                                                      style={{ opacity: 0.5 }}>
                                                      {message.message.substring(
                                                        selectedMessageForInterruption
                                                          .meta
                                                          .tempInterruptionPosition as number
                                                      )}
                                                    </span>
                                                  </>
                                                );
                                              } else {
                                                return message.message;
                                              }
                                            })()
                                          : JSON.stringify(message.message)}
                                      </div>
                                    </Tooltip>
                                  ) : (
                                    <div
                                      style={{
                                        cursor: interruptionMode
                                          ? "text"
                                          : "auto",
                                        position: "relative",
                                      }}>
                                      {typeof message.message === "string"
                                        ? (() => {
                                            // Check if this message has an interruption point
                                            const interruptionPoint =
                                              interruptionPoints.find(
                                                (p) =>
                                                  p.messageId ===
                                                  String(
                                                    message.meta
                                                      ?.reference_id ||
                                                      message.id
                                                  )
                                              );

                                            const hasInterruptionPoint =
                                              !!interruptionPoint;

                                            if (
                                              hasInterruptionPoint &&
                                              interruptionPoint?.position !==
                                                null
                                            ) {
                                              return (
                                                <>
                                                  <span>
                                                    {message.message.substring(
                                                      0,
                                                      interruptionPoint.position ||
                                                        0
                                                    )}
                                                  </span>
                                                  <span
                                                    className="interruption-marker"
                                                    style={{
                                                      display: "inline-block",
                                                      width: "2px",
                                                      height: "1em",
                                                      backgroundColor: "red",
                                                      verticalAlign: "middle",
                                                      marginLeft: "2px",
                                                      marginRight: "2px",
                                                    }}></span>
                                                  <span
                                                    style={{
                                                      textDecoration:
                                                        "line-through",
                                                      color: "#999",
                                                    }}>
                                                    {message.message.substring(
                                                      interruptionPoint.position ||
                                                        0
                                                    )}
                                                  </span>
                                                </>
                                              );
                                            } else if (
                                              selectedMessageForInterruption
                                                ?.meta?.reference_id ===
                                                message.meta?.reference_id &&
                                              typeof selectedMessageForInterruption
                                                ?.meta
                                                ?.tempInterruptionPosition ===
                                                "number"
                                            ) {
                                              return (
                                                <>
                                                  <span>
                                                    {message.message.substring(
                                                      0,
                                                      selectedMessageForInterruption
                                                        .meta
                                                        .tempInterruptionPosition as number
                                                    )}
                                                  </span>
                                                  <span
                                                    className="interruption-marker"
                                                    style={{
                                                      display: "inline-block",
                                                      width: "2px",
                                                      height: "1em",
                                                      backgroundColor: "orange",
                                                      verticalAlign: "middle",
                                                      marginLeft: "2px",
                                                      marginRight: "2px",
                                                    }}></span>
                                                  <span
                                                    style={{ opacity: 0.5 }}>
                                                    {message.message.substring(
                                                      selectedMessageForInterruption
                                                        .meta
                                                        .tempInterruptionPosition as number
                                                    )}
                                                  </span>
                                                </>
                                              );
                                            } else {
                                              return message.message;
                                            }
                                          })()
                                        : JSON.stringify(message.message)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {isTyping && (
                <div className="typing-indicator">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              )}
            </>
          )}
        </div>
        <div className="inputContainer">
          <Input
            ref={inputRef}
            placeholder="Type a message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onPressEnter={() => handleSend(null)}
            disabled={
              session === undefined ||
              (assistantFirst === true && session === undefined) ||
              isTyping
            }
          />
          <Button
            icon={isRecording ? undefined : <AudioOutlined />}
            style={{
              marginLeft: "0px",
              marginRight: "8px",
              position: "relative",
              overflow: "hidden",
            }}
            onClick={isRecording ? stopRecording : startRecording}
            danger={isRecording}
            disabled={
              session === undefined ||
              (assistantFirst === true && session === undefined) ||
              isTyping
            }>
            <AudioVisualization
              levels={audioLevels}
              isRecording={isRecording}
            />
          </Button>
          <Button
            type="primary"
            onClick={() => handleSend(null)}
            disabled={
              session === undefined ||
              (assistantFirst === true && session === undefined) ||
              isTyping
            }>
            Send
          </Button>
        </div>
        <Tour
          open={tourOpen}
          onClose={() => setTourOpen(false)}
          steps={tourSteps}
        />
      </div>
    );
  }
);

// Helper function to render the performance bar
const renderPerformanceBar = (
  timings: ModelTiming[],
  segments: any[],
  getModelLabelFn: (modelName: string) => React.ReactNode
) => {
  if (timings.length === 0 || segments.length === 0) return null;

  // Get the max time value from the first segment (all segments share the same maxTimeMs)
  const maxTimeMs = segments[0]?.maxTimeMs || 500;

  return (
    <div
      style={{
        margin: "8px 0 12px 0",
      }}>
      {/* Stacked progress bar */}
      <div
        style={{
          height: "12px",
          width: "100%",
          background: "rgba(0,0,0,0.1)",
          borderRadius: "3px",
          overflow: "hidden",
          display: "flex",
          position: "relative",
        }}>
        {/* 250ms threshold line */}
        {maxTimeMs > 250 && (
          <div
            style={{
              position: "absolute",
              left: `${(250 / maxTimeMs) * 100}%`,
              top: 0,
              height: "100%",
              width: "2px",
              background: "rgba(255, 0, 0, 0.7)",
              zIndex: 2,
              boxShadow: "0 0 2px rgba(255, 0, 0, 0.5)",
            }}
          />
        )}

        {segments.map((segment, idx) => (
          <Tooltip
            key={`${segment.model}-${idx}`}
            title={
              <>
                {getModelLabelFn(segment.model)}
                <br />
                {segment.isComplete
                  ? `${
                      segment.metricType === "ttft"
                        ? "Time to first token"
                        : "Inference time"
                    }: ${segment.duration}ms`
                  : segment.metricType === "ttft"
                  ? "Waiting for first token..."
                  : "Waiting for inference time..."}
              </>
            }>
            <div
              style={{
                width: `${segment.percent}%`,
                height: "100%",
                background: segment.color,
                position: "absolute",
                left: `${segment.startPercent}%`,
                transition: "width 0.3s ease, left 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                opacity: segment.isComplete ? 1 : 0.7,
                animation: !segment.isComplete ? "pulse 1.5s infinite" : "none",
              }}>
              {segment.percent > 15 && (
                <span
                  style={{
                    color: "#fff",
                    fontSize: "8px",
                    fontWeight: "bold",
                    textShadow: "0 0 2px rgba(0,0,0,0.5)",
                    padding: "0 3px",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    lineHeight: "1",
                    transform: "translateY(-0.5px)",
                  }}>
                  {segment.model.split(":").pop()}
                </span>
              )}
            </div>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default SyncChatComponent;
